import React from "react";
import { Link } from "react-router-dom";
import "./PLButton.scss";

interface PLButtonProps {
  href?: string; // Optional string for the href attribute
  children: React.ReactNode; // React children nodes
  className?: string; // Optional string for additional CSS classes
  onClick?: (e: any) => void; // Optional function for the onClick event
  target?: string; // Optional target attribute for links
  ariaLabel?: string; // Optional aria-label for accessibility and SEO
  variant?: "primary" | "secondary" | "green" | "blue"; // Button variants
  size?: "sm" | "md" | "lg"; // Button sizes
  disabled?: boolean; // Disabled state
  type?: "button" | "submit" | "reset"; // Button type attribute
}

export const PLButton: React.FC<PLButtonProps> = ({
  href,
  children,
  className,
  onClick,
  target,
  ariaLabel,
  variant = "primary",
  size = "md",
  disabled = false,
  type = "button",
}) => {
  const baseClasses = `pl-button pl-button--${variant} pl-button--${size}`;
  const combinedClasses = `${baseClasses} ${className || ""}`.trim();

  if (href) {
    // Check if it's an external URL (starts with http/https)
    const isExternal =
      href.startsWith("http://") || href.startsWith("https://");
    // Check if it's an anchor link (starts with #)
    const isAnchor = href.startsWith("#");

    if (isExternal) {
      return (
        <a
          href={href}
          className={combinedClasses}
          target={target || "_blank"}
          rel="noopener noreferrer"
          aria-label={ariaLabel}
        >
          {children}
        </a>
      );
    } else if (isAnchor) {
      return (
        <a
          href={href}
          className={combinedClasses}
          onClick={(e) => {
            e.preventDefault();
            const element = document.querySelector(href);
            if (element) {
              element.scrollIntoView({ behavior: "smooth" });
            }
            if (onClick) onClick(e);
          }}
          aria-label={ariaLabel}
        >
          {children}
        </a>
      );
    } else {
      return (
        <Link
          to={href}
          className={combinedClasses}
          aria-label={ariaLabel}
          onClick={onClick}
        >
          {children}
        </Link>
      );
    }
  }

  return (
    <button
      className={combinedClasses}
      onClick={onClick}
      aria-label={ariaLabel}
      disabled={disabled}
      type={type}
    >
      {children}
    </button>
  );
};

export default PLButton;
